import React from "react";
import { MemoryRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { FolderKanban, Layers, Puzzle } from "lucide-react";
import ProjectsPage from "./pages/ProjectsPage";
import WorkspacePage from "./pages/WorkspacePage";
import TemplatesPage from "./pages/TemplatesPage";
import PluginsPage from "./pages/PluginsPage";
import PluginDetailPage from "./pages/PluginDetailPage";

const NAV = [
  { path: "/", label: "Projects", icon: FolderKanban, testid: "est-nav-projects" },
  { path: "/templates", label: "Templates", icon: Layers, testid: "est-nav-templates" },
  { path: "/plugins", label: "Plugins", icon: Puzzle, testid: "est-nav-plugins" },
];

/**
 * Module nav strip — replaces the source app's TopNav (which was global
 * app chrome and intentionally not ported). Hidden inside the workspace
 * (that page has its own Home breadcrumb) so the canvas keeps full height.
 */
function ModuleNav() {
  const navigate = useNavigate();
  const location = useLocation();
  if (location.pathname.startsWith("/workspace/")) return null;
  return (
    <div
      data-testid="estimating-module-nav"
      className="shrink-0 flex items-center gap-1 px-3 sm:px-6 py-2 bg-[#0a0a0a] border-b border-white/15"
    >
      {NAV.map(({ path, label, icon: Icon, testid }) => {
        const active = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
        return (
          <button
            key={path}
            type="button"
            data-testid={testid}
            onClick={() => navigate(path)}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
              active
                ? "bg-[#1a3fd1]/15 text-[#8fa4f0] border border-[#1a3fd1]/40"
                : "text-slate-400 hover:text-white border border-transparent hover:bg-white/5"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * EstimatingApp — the ported Steeltooth estimating subsystem, mounted
 * inside Elite's OLD estimating tab (contained "app within the app").
 *
 * A MemoryRouter keeps the module's internal navigation ("/workspace/:id",
 * "/plugins", "/templates", ...) verbatim without touching Elite's URL —
 * the pages were ported unchanged, including their absolute navigate()
 * calls, which resolve inside this router.
 */
/**
 * "Send to Estimate" from the takeoff module hands over a project id (event
 * when this root is already mounted, sessionStorage when it mounts fresh):
 * open that project's workspace, then clear the one-shot handoff.
 */
function ExternalOpen() {
  const navigate = useNavigate();
  React.useEffect(() => {
    const pending = sessionStorage.getItem("est_open_project");
    if (pending) { sessionStorage.removeItem("est_open_project"); navigate(`/workspace/${pending}`); }
    const h = (e) => {
      const id = e.detail?.projectId;
      if (id) { sessionStorage.removeItem("est_open_project"); navigate(`/workspace/${id}`); }
    };
    window.addEventListener("elite:open-ai-estimate", h);
    return () => window.removeEventListener("elite:open-ai-estimate", h);
  }, [navigate]);
  return null;
}

export default function EstimatingApp() {
  return (
    <MemoryRouter initialEntries={[sessionStorage.getItem("est_open_project") ? `/workspace/${sessionStorage.getItem("est_open_project")}` : "/"]}>
      <ExternalOpen />
      <div className="h-full min-h-0 flex flex-col bg-[#0a0a0a]">
        <ModuleNav />
        <div className="flex-1 min-h-0 flex flex-col">
          <Routes>
            <Route path="/" element={<ProjectsPage />} />
            <Route path="/workspace/:projectId" element={<WorkspacePage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/reports" element={<Navigate to="/templates" replace />} />
            <Route path="/plugins" element={<PluginsPage />} />
            <Route path="/plugins/:pluginId" element={<PluginDetailPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </MemoryRouter>
  );
}
